<template>
  <!--
    VoiceEditor — read-only prose for each of the four sections, with
    a pencil-style FieldHelpIcon that opens Aida scoped to the field.
    Aida's polish output lands here via the
    ``studio-assistant:field-staged`` window event, which stages a
    "Proposed" card with Discard / Publish. Publish updates the parent
    model + emits ``publish-section`` so the parent persists.

    Mirrors the teaser/description pattern in ProfilePanel exactly,
    so the Mystic experience reads the same: never type into a raw
    textarea on the panel — always go through Aida.
  -->
  <div class="voice-editor">
    <div
      v-for="section in sections"
      :key="section.key"
      class="voice-section"
    >
      <div class="voice-section-head">
        <span class="voice-section-label">{{ section.label }}</span>
        <FieldHelpIcon
          :field="`voice_${section.key}`"
          :label="section.label"
          panel="profile"
          :value="props.modelValue[section.key] || ''"
          variant="pencil"
          :title="section.helpTitle"
        />
      </div>
      <p class="voice-section-hint">{{ section.hint }}</p>

      <p
        v-if="(props.modelValue[section.key] || '').trim()"
        class="voice-section-text"
      >{{ props.modelValue[section.key] }}</p>
      <p v-else class="voice-section-empty">{{ section.emptyHint }}</p>

      <div v-if="drafts[section.key] !== null" class="draft-card">
        <div class="draft-label">Proposed</div>
        <p class="draft-text">{{ drafts[section.key] || '(empty)' }}</p>
        <div class="draft-actions">
          <button
            class="ghost-btn-sm"
            :disabled="publishing[section.key]"
            @click="discardDraft(section.key)"
          >Discard</button>
          <button
            class="primary-btn-sm"
            :disabled="publishing[section.key] || !drafts[section.key].trim()"
            @click="publishDraft(section.key)"
          >{{ publishing[section.key] ? 'Publishing…' : 'Publish' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, onMounted, onUnmounted } from 'vue'
import FieldHelpIcon from './FieldHelpIcon.vue'

const props = defineProps({
  modelValue: { type: Object, required: true },
})
const emit = defineEmits(['update:modelValue', 'publish-section'])

const sections = [
  {
    key: 'tone', label: 'Tone',
    hint: "Two or three words for how this reader feels - warm, hushed, fierce, playful, grounded.",
    emptyHint: 'Click the pencil to ask Aida to help you describe the tone.',
    helpTitle: 'Help me describe my reader’s tone',
  },
  {
    key: 'style', label: 'Speaking style',
    hint: 'How they speak - cadence and vocabulary. Poetic or direct? Long sentences or clipped?',
    emptyHint: 'Click the pencil to shape how this reader speaks.',
    helpTitle: 'Help me shape my reader’s speaking style',
  },
  {
    key: 'specialty', label: 'What they specialize in',
    hint: 'Who do they read for, and what transformation do they offer?',
    emptyHint: 'Click the pencil to describe who this reader serves.',
    helpTitle: 'Help me describe what my reader specializes in',
  },
  {
    key: 'boundaries', label: 'Boundaries',
    hint: "What this reader won't do. Aida will gently test these - narrow boundaries shrink your audience.",
    emptyHint: 'Click the pencil to set what this reader explicitly won’t do.',
    helpTitle: 'Help me set my reader’s boundaries',
  },
]

// Per-section draft state. null = no proposal staged; string = a
// proposal Aida produced via studio-assistant:field-staged. Mirrors
// teaserDraft / descriptionDraft in ProfilePanel.
const drafts = reactive({
  tone: null, style: null, specialty: null, boundaries: null,
})
// Per-section publish-in-flight flag (avoids double-clicks).
const publishing = reactive({
  tone: false, style: false, specialty: false, boundaries: false,
})

function onFieldStaged(e) {
  const { field, value } = e.detail || {}
  if (!field || value === undefined) return
  // Only handle voice_<key> fields. Ignore Profile's teaser/desc.
  const stripped = field.replace(/^voice_/, '')
  if (!Object.prototype.hasOwnProperty.call(drafts, stripped)) return
  if (stripped === field) return  // didn't have voice_ prefix
  drafts[stripped] = value
}

function discardDraft(key) {
  drafts[key] = null
}

async function publishDraft(key) {
  if (publishing[key]) return
  const proposed = drafts[key]
  if (proposed === null) return
  publishing[key] = true
  try {
    // Update the parent's modelValue first so the displayed prose
    // flips immediately.
    emit('update:modelValue', { ...props.modelValue, [key]: proposed })
    // Ask the parent to persist. Parent's handler PATCHes the BE
    // and resolves once the save is done.
    await emitPublishSection(key, proposed)
    drafts[key] = null
  } finally {
    publishing[key] = false
  }
}

// Tiny adapter so the parent can pass a Promise back via the event
// payload's resolve callback. Vue's emit doesn't natively wait for a
// listener's return value, so we wrap with a one-shot Promise.
function emitPublishSection(key, value) {
  return new Promise((resolve, reject) => {
    emit('publish-section', { section: key, value, resolve, reject })
  })
}

onMounted(() => {
  window.addEventListener('studio-assistant:field-staged', onFieldStaged)
})
onUnmounted(() => {
  window.removeEventListener('studio-assistant:field-staged', onFieldStaged)
})

defineExpose({ drafts })
</script>

<style scoped>
@import '@/styles/studio-forms.css';

.voice-editor {
  display: flex;
  flex-direction: column;
  gap: 22px;
}
.voice-section { display: flex; flex-direction: column; gap: 4px; }
.voice-section-head {
  display: flex;
  align-items: center;
  gap: 8px;
}
.voice-section-label {
  font-family: 'Cinzel', serif;
  font-size: 13px;
  letter-spacing: 0.4px;
  color: #C9A94E;
  font-weight: 600;
}
.voice-section-hint {
  margin: 0 0 4px;
  font-size: 12px;
  color: rgba(195,169,255,0.5);
  font-style: italic;
}
.voice-section-text {
  margin: 4px 0 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 15px;
  line-height: 1.55;
  color: rgba(232, 216, 255, 0.92);
  white-space: pre-wrap;
}
.voice-section-empty {
  margin: 4px 0 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-size: 14px;
  color: rgba(195, 169, 255, 0.4);
}

.draft-card {
  margin-top: 10px;
  padding: 12px 14px;
  background: rgba(0, 0, 0, 0.28);
  border: 1px dashed rgba(201, 169, 78, 0.45);
  border-radius: 10px;
}
.draft-label {
  font-family: 'Cinzel', serif;
  font-size: 11px;
  letter-spacing: 0.5px;
  color: rgba(201, 169, 78, 0.85);
  margin-bottom: 6px;
}
.draft-text {
  margin: 0 0 10px;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 14px;
  color: rgba(232, 216, 255, 0.88);
  white-space: pre-wrap;
}
.draft-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
.ghost-btn-sm {
  padding: 5px 12px;
  font-size: 12px;
  background: transparent;
  color: rgba(195,169,255,0.85);
  border: 1px solid rgba(195,169,255,0.25);
  border-radius: 6px;
  cursor: pointer;
}
.ghost-btn-sm:disabled { opacity: 0.4; cursor: not-allowed; }
.primary-btn-sm {
  padding: 5px 14px;
  font-size: 12px;
  background: linear-gradient(135deg, #c9a94e, #a07a2c);
  color: #1a0e0e;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
}
.primary-btn-sm:hover:not(:disabled) { filter: brightness(1.06); }
.primary-btn-sm:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
